import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useApp, LANG_SHORT } from "@/context/AppContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { Languages, Moon, Sun, Building2, ArrowRight, MailCheck } from "lucide-react";

export default function Register() {
  const { t, cycleLang, setLang, lang, theme, toggleTheme } = useApp();
  const navigate = useNavigate();
  const [apartments, setApartments] = useState([]);
  const [form, setForm] = useState({ name: "", apartment: "", phone: "", email: "", pin: "", pin2: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get("/auth/apartments")
      .then(({ data }) => setApartments(data.apartments || []))
      .catch(() => setApartments([]));
  }, []);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const submit = async (e) => {
    e?.preventDefault();
    if (!form.name.trim() || !form.apartment || !form.phone.trim() || !form.email.trim()) {
      toast.error(t("reg_fields_required")); return;
    }
    if (form.pin !== form.pin2) { toast.error(t("reg_pin_mismatch")); return; }
    setBusy(true);
    try {
      await api.post("/auth/register", {
        name: form.name.trim(), apartment: form.apartment, phone: form.phone.trim(),
        email: form.email.trim(), pin: form.pin, lang,
      });
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("login_error"));
    } finally { setBusy(false); }
  };

  const field = "w-full h-14 rounded-2xl bg-white text-foreground px-5 text-base outline-none";

  return (
    <div className="min-h-screen bg-brand text-white relative overflow-hidden flex flex-col">
      <div className="absolute inset-0 zellige-overlay pointer-events-none" />
      <div className="relative z-10 flex justify-end gap-2 p-4">
        <button onClick={cycleLang} data-testid="register-lang-toggle"
          className="h-10 px-3 rounded-full bg-white/15 font-semibold text-sm flex items-center gap-1.5 [&>*]:pointer-events-none">
          <Languages className="h-4 w-4" />{LANG_SHORT[lang]}
        </button>
        <button onClick={toggleTheme} data-testid="register-theme-toggle"
          className="h-10 w-10 rounded-full bg-white/15 flex items-center justify-center">
          {theme === "light" ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
        </button>
      </div>

      <div className="relative z-10 flex-1 flex flex-col justify-center px-6 max-w-md w-full mx-auto pb-10">
        {done ? (
          <div className="text-center" data-testid="register-success">
            <div className="h-16 w-16 rounded-2xl bg-white/15 flex items-center justify-center mb-6 mx-auto">
              <MailCheck className="h-9 w-9" />
            </div>
            <h1 className="text-3xl font-black mb-3">{t("register_success_title")}</h1>
            <p className="text-white/80 text-base mb-8">{t("register_success_msg")}</p>
            <button onClick={() => navigate("/login")} data-testid="register-back-login"
              className="w-full h-14 rounded-2xl bg-majorelle text-white font-bold text-lg">
              {t("back_to_login")}
            </button>
          </div>
        ) : (
          <>
            <div className="h-16 w-16 rounded-2xl bg-white/15 flex items-center justify-center mb-6">
              <Building2 className="h-9 w-9" />
            </div>
            <h1 className="text-4xl font-black tracking-tight mb-2">{t("register_title")}</h1>
            <p className="text-white/70 text-lg mb-8">{t("register_subtitle")}</p>

            <form onSubmit={submit} className="space-y-3">
              {/* Choix explicite de la langue — toute l'expérience (site + e-mails) la suivra */}
              <div className="rounded-2xl bg-white/10 p-4" data-testid="register-lang-block">
                <p className="text-sm font-bold mb-2 flex items-center gap-1.5">
                  <Languages className="h-4 w-4" /> {t("reg_lang_label")}
                </p>
                <div className="grid grid-cols-3 gap-2">
                  {[["fr", "Français"], ["ar", "العربية"], ["en", "English"]].map(([l, lbl]) => (
                    <button key={l} type="button" onClick={() => setLang(l)} data-testid={`register-lang-${l}`}
                      className={`h-11 rounded-xl font-bold text-sm transition-colors ${lang === l ? "bg-white text-brand" : "bg-white/10 text-white"}`}>
                      {lbl}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-white/70 mt-2">{t("reg_lang_hint")}</p>
              </div>

              <input type="text" value={form.name} onChange={(e) => set({ name: e.target.value })}
                placeholder={t("reg_name_ph")} data-testid="register-name" className={field} required />

              <select value={form.apartment} onChange={(e) => set({ apartment: e.target.value })}
                data-testid="register-apartment"
                className={`${field} ${form.apartment ? "" : "text-muted-foreground"}`} required>
                <option value="">{t("reg_select_apartment")}</option>
                {apartments.map((a) => <option key={a} value={a}>{a}</option>)}
              </select>

              <input type="tel" value={form.phone} onChange={(e) => set({ phone: e.target.value })}
                placeholder={t("reg_phone_ph")} data-testid="register-phone" className={field} required />

              <input type="email" value={form.email} onChange={(e) => set({ email: e.target.value })}
                placeholder={t("reg_email_ph")} data-testid="register-email"
                autoCapitalize="none" autoCorrect="off" spellCheck="false" className={field} required />

              <input type="password" inputMode="numeric" maxLength={6} value={form.pin}
                onChange={(e) => set({ pin: e.target.value.replace(/[^0-9]/g, "") })}
                placeholder={t("reg_pin_ph")} data-testid="register-pin"
                className={`${field} tracking-[0.3em]`} required />

              <input type="password" inputMode="numeric" maxLength={6} value={form.pin2}
                onChange={(e) => set({ pin2: e.target.value.replace(/[^0-9]/g, "") })}
                placeholder={t("reg_pin_confirm_ph")} data-testid="register-pin2"
                className={`${field} tracking-[0.3em]`} required />

              <button type="submit" disabled={busy} data-testid="register-submit"
                className="w-full h-14 rounded-2xl bg-majorelle text-white font-bold text-lg flex items-center justify-center gap-2 active:scale-[0.98] transition-transform disabled:opacity-60">
                {busy ? t("loading") : t("register_submit")}
                {!busy && <ArrowRight className="h-5 w-5 rtl:rotate-180" />}
              </button>
            </form>

            <button onClick={() => navigate("/login")} data-testid="register-have-account"
              className="w-full h-12 rounded-2xl border border-white/30 mt-6 font-semibold">
              {t("have_account")}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
